import React from 'react';
import { Collapse, List, Space, Tag, Typography, theme } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { formatDate } from './GeoExposureLayout';

const { Text, Paragraph } = Typography;

const LINK_KIND_LABELS = { citation: 'Cited', search: 'Searched', text: 'In text' };

export const MentionTag = ({ answer }) => {
    if (answer.status !== 'complete') return <Tag color="error">{answer.status === 'pending' ? 'Pending' : 'Failed'}</Tag>;
    if (answer.mentioned === true) return <Tag color="success">Mentioned</Tag>;
    if (answer.mentioned === false) return <Tag>Not mentioned</Tag>;
    return <Tag color="warning">Unchecked</Tag>;
};

const LinksList = ({ links }) => (
    <List
        size="small"
        dataSource={links}
        renderItem={(link) => (
            <List.Item style={{ padding: '4px 0' }}>
                <Space size={6} style={{ minWidth: 0 }}>
                    <Tag style={{ marginRight: 0 }}>{LINK_KIND_LABELS[link.kind] || link.kind}</Tag>
                    <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                        <LinkOutlined /> {link.title || link.url}
                    </a>
                </Space>
            </List.Item>
        )}
    />
);

/** One rephrase and what ChatGPT answered, with the mention verdict and every link it gave. */
export const AnswerCard = ({ answer }) => {
    const { token } = theme.useToken();
    const citations = answer.links.filter((l) => l.kind !== 'text');
    const header = (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text strong>{answer.position + 1}. {answer.prompt}</Text>
            <Space size={4} wrap>
                <MentionTag answer={answer} />
                {answer.status === 'complete' && (
                    <Tag color={citations.length ? 'blue' : 'default'}>{citations.length ? `${citations.length} citation${citations.length === 1 ? '' : 's'}` : 'No citations'}</Tag>
                )}
                {answer.status === 'complete' && answer.links.length > citations.length && <Tag>{answer.links.length - citations.length} link{answer.links.length - citations.length === 1 ? '' : 's'} in text</Tag>}
                {answer.webSearchTriggered === false && <Tag>No web search</Tag>}
                {answer.attempts > 1 && <Tag>{answer.attempts} attempts</Tag>}
            </Space>
        </Space>
    );
    const body = (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {answer.mentioned === true && (
                <Text type="success">
                    Mention ({answer.mentionSource === 'llm' ? 'judged by the model' : answer.mentionSource === 'link' ? 'a link to our site' : 'brand term found'}): “{answer.mentionEvidence}”
                </Text>
            )}
            {answer.mentioned === false && answer.mentionEvidence && <Text type="secondary">Model: {answer.mentionEvidence}</Text>}
            {answer.error && <Text type="danger">{answer.error}</Text>}
            {answer.answerText && (
                <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0, background: token.colorFillQuaternary, padding: 12, borderRadius: token.borderRadius }}>{answer.answerText}</Paragraph>
            )}
            {answer.links.length > 0 && (
                <div>
                    <Text strong>Citations and links</Text>
                    <LinksList links={answer.links} />
                </div>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>Answered {formatDate(answer.updatedAt)}</Text>
        </Space>
    );
    return <Collapse items={[{ key: answer.id, label: header, children: body }]} />;
};
